import React, { ReactNode } from 'react'
import styled from 'styled-components'

/** Height of a single CUI banner. */
export const CLASSIFICATION_BANNER_HEIGHT = '28px'

/**
 * Name of the CSS custom property this component sets while the classification
 * banners are shown. It holds the total vertical space (top + bottom) reserved
 * by the banners
 */
const CLASSIFICATION_OFFSET_VAR = '--cantilever-classification-offset'

/**
 * A `100vh` expression adjusted for the classification banners. Use this
 * anywhere you would otherwise write `100vh` for a full-viewport-height
 * element so it never renders underneath the fixed banners.
 * Resolves to plain `100vh` when no banner is shown.
 *
 * @example
 * const Page = styled.div`height: ${classificationSafeVh};`
 */
export const classificationSafeVh = `calc(100vh - var(${CLASSIFICATION_OFFSET_VAR}, 0px))`

const CuiPurple = '#502b85'
const UnclassifiedGreen = '#007A33'

const ClassificationBanner = styled.div<{
  $position: 'top' | 'bottom'
  $background: string
}>`
  position: fixed;
  ${({ $position }) => $position}: 0;
  left: 0;
  right: 0;
  z-index: 2147483647;
  width: 100%;
  height: ${CLASSIFICATION_BANNER_HEIGHT};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $background }) => $background};
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  text-transform: none;
  box-shadow: none;
`

const ClassificationContent = styled.div`
  ${CLASSIFICATION_OFFSET_VAR}: calc(${CLASSIFICATION_BANNER_HEIGHT} * 2);
  box-sizing: border-box;
  height: 100vh;
  min-height: 0;
  padding-top: ${CLASSIFICATION_BANNER_HEIGHT};
  padding-bottom: ${CLASSIFICATION_BANNER_HEIGHT};
  overflow: hidden;
`

const shouldShowCuiBanner = (environment: string) =>
  environment.startsWith('govgecko') || environment.startsWith('unnpi')

/**
 * Wraps an application in CUI (Controlled Unclassified Information)
 * classification banners when running in a government environment.
 *
 * The host application is responsible for determining the current
 * environment and passing it in via the `environment` prop, keeping this
 * component free of any app-specific runtime dependencies.
 *
 * When banners are shown, the wrapper sets {@link CLASSIFICATION_OFFSET_VAR} to
 * the reserved space; full-viewport-height layouts nested in the children
 * should size themselves with {@link classificationSafeVh} so they never
 * render behind the banners.
 */
export const CantileverClassificationFrame: React.FC<{
  children: ReactNode
  environment: string
}> = ({ children, environment }) => {
  if (!shouldShowCuiBanner(environment)) {
    return <>{children}</>
  }

  const isGovSandboxEnv = environment === 'govgecko-sandbox'
  const label = isGovSandboxEnv ? (
    <p>
      SANDBOX: <strong>NOT</strong> CUI COMPLIANT - DO NOT USE WITH PROTECTED
      GOVERNMENT DATA
    </p>
  ) : (
    'CUI'
  )
  const background = isGovSandboxEnv ? UnclassifiedGreen : CuiPurple

  return (
    <>
      <ClassificationBanner
        $position='top'
        $background={background}
        role='note'>
        {label}
      </ClassificationBanner>
      <ClassificationContent>{children}</ClassificationContent>
      <ClassificationBanner
        $position='bottom'
        $background={background}
        role='note'>
        {label}
      </ClassificationBanner>
    </>
  )
}
